// BL-133, calendar B: a week of arrival windows on Book (pages/ux-full schedule/book, cal=b). The
// windows down, the days across, each cell the technician time still open in it for the work picked:
// muted when nobody can fit the visit's length, hatched when full, "Closed" when the office closed it,
// blank once over (the one under way is still booked into, QA-829). One tap picks the day and the window together. A week a page, from one
// capacity.availability read each; "Soonest that fits" and a day to jump to cover the weeks after.
// Arrow keys move between cells and Enter picks. A full or closed window can still be picked, as
// before: Book warns, and the dispatcher sees it (BL-057).

import { useRef, useState, type CSSProperties, type KeyboardEvent } from 'react';
import {
  dayLabel,
  hoursWords,
  moveCell,
  WEEK_PAGES,
  weekPageOf,
  weekRangeWords,
  windowLabel,
  type WeekCell,
  type WeekGrid,
} from './book-model.ts';

/** "8–10" over "a.m.": a window's row heading, narrow enough for a phone. */
function windowHead([s, e]: readonly [number, number]): { range: string; part: string } {
  const label = windowLabel([s, e]);
  const cut = label.lastIndexOf(' ');
  return { range: label.slice(0, cut), part: label.slice(cut + 1) };
}

export function BookWeek({
  today,
  last,
  page,
  grid,
  failed,
  chosen,
  soonest,
  length,
  onPage,
  onPick,
  onJump,
}: {
  today: string;
  /** The last day booking offers. */
  last: string;
  page: number;
  /** This page's grid, once its read is in (a failed read still gives one, every cell unknown). */
  grid: WeekGrid | undefined;
  failed: boolean;
  chosen: { date: string; window: readonly [number, number] | null };
  soonest: { found: WeekCell } | { need: number } | { none: true } | { unknown: true };
  /** The job type's length, when one is picked. */
  length: number | null;
  onPage: (page: number) => void;
  onPick: (c: WeekCell) => void;
  onJump: (date: string) => void;
}) {
  const gridRef = useRef<HTMLDivElement>(null);
  const [focus, setFocus] = useState<{ page: number; row: number; col: number } | null>(null);
  const [jumpError, setJumpError] = useState<string | null>(null);
  const cols = grid?.days.length ?? 0;
  const rows = grid?.rows.length ?? 0;
  // one cell takes Tab (a roving tab stop): the last one moved to, else the chosen one, else the first
  // that fits, else the first
  const chosenAt = (() => {
    if (!grid) return null;
    const col = grid.days.findIndex((x) => x.date === chosen.date);
    const row = chosen.window ? grid.rows.findIndex((r) => r.window[0] === chosen.window![0]) : -1;
    return col >= 0 && row >= 0 ? { row, col } : null;
  })();
  const firstFit = (() => {
    if (!grid) return null;
    for (let col = 0; col < cols; col++)
      for (let row = 0; row < rows; row++)
        if (grid.rows[row]!.cells[col]!.state === 'open') return { row, col };
    return null;
  })();
  const stop =
    (focus && focus.page === page ? focus : null) ??
    chosenAt ??
    firstFit ??
    (grid ? { row: 0, col: 0 } : null);

  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const t = (e.target as HTMLElement).closest<HTMLElement>('[data-cell]');
    if (!t || !grid) return;
    const [row, col] = t.dataset.cell!.split(':').map(Number) as [number, number];
    const to = moveCell({ row, col }, e.key, { rows, cols });
    if (!to) return;
    e.preventDefault();
    setFocus({ page, ...to });
    gridRef.current?.querySelector<HTMLElement>(`[data-cell="${to.row}:${to.col}"]`)?.focus();
  };

  const forLength = length !== null ? ` ${hoursWords(length)}` : '';
  const range = grid ? weekRangeWords(grid.from, grid.to) : '';
  return (
    <div className="bk-cal">
      <p className="bk-soon">
        {'found' in soonest ? (
          <>
            {length !== null ? `Soonest that fits${forLength}` : 'Soonest open'}:{' '}
            <button type="button" className="bk-link" onClick={() => onPick(soonest.found)}>
              {dayLabel(soonest.found.date, today)}, {windowLabel(soonest.found.window)}
            </button>
          </>
        ) : 'none' in soonest ? (
          `${length !== null ? `Nothing fits${forLength}` : 'Nothing is open'} in the next ${WEEK_PAGES} weeks.`
        ) : 'unknown' in soonest ? (
          'Couldn’t find the soonest window. Pick one below.'
        ) : (
          'Finding the soonest window that fits…'
        )}
      </p>
      <div className="bk-cal-h">
        <button
          type="button"
          className="bk-link"
          disabled={page <= 0}
          onClick={() => {
            setFocus(null);
            onPage(page - 1);
          }}
        >
          Earlier
        </button>
        <b className="bk-range" aria-live="polite">
          {range}
        </b>
        <button
          type="button"
          className="bk-link"
          disabled={page >= WEEK_PAGES - 1}
          onClick={() => {
            setFocus(null);
            onPage(page + 1);
          }}
        >
          Later
        </button>
      </div>
      {!grid ? (
        <p className="muted small">Getting the open times…</p>
      ) : (
        <>
          {failed && (
            <p className="muted small">
              Couldn’t get the open times for this week. You can still pick a window.
            </p>
          )}
          <div
            ref={gridRef}
            className="bk-week"
            role="grid"
            aria-label={`Open time by arrival window, ${range}`}
            style={{ '--days': cols } as CSSProperties}
            onKeyDown={onKey}
          >
            <div role="row" className="bk-row">
              <span role="columnheader" className="bk-corner" aria-label="Arrival window" />
              {grid.days.map((x) => (
                <span
                  key={x.date}
                  role="columnheader"
                  className={`bk-dh${x.today ? ' is-today' : ''}${x.date === chosen.date ? ' is-on' : ''}`}
                  aria-label={dayLabel(x.date, today)}
                >
                  <b>
                    {x.today ? (
                      <>
                        <span className="bk-wide">Today</span>
                        <span className="bk-narrow">{x.short}</span>
                      </>
                    ) : (
                      x.day
                    )}
                  </b>
                  <span>{x.num}</span>
                </span>
              ))}
            </div>
            {grid.rows.map((r, row) => {
              const h = windowHead(r.window);
              return (
                <div role="row" className="bk-row" key={r.window[0]}>
                  <span role="rowheader" className="bk-wh" aria-label={r.label}>
                    {h.range}
                    <small>{h.part}</small>
                  </span>
                  {r.cells.map((c, col) => {
                    const on = chosen.date === c.date && chosen.window?.[0] === c.window[0];
                    const past = c.state === 'past';
                    return (
                      <span role="gridcell" key={c.date} className="bk-gc">
                        <button
                          type="button"
                          className={`bk-wc is-${c.state}`}
                          data-cell={`${row}:${col}`}
                          tabIndex={stop && stop.row === row && stop.col === col ? 0 : -1}
                          aria-pressed={on}
                          aria-disabled={past || undefined}
                          aria-label={c.name}
                          onFocus={() => setFocus({ page, row, col })}
                          onClick={() => !past && onPick(c)}
                        >
                          {c.state === 'closed' ? <span className="bk-wide">Closed</span> : c.text}
                        </button>
                      </span>
                    );
                  })}
                </div>
              );
            })}
          </div>
          <p className="muted small bk-key">
            Hours of technician time open in each arrival window.
            {length !== null &&
              ` Muted: there’s room, but nobody can fit ${hoursWords(length)} starting in it.`}{' '}
            Hatched: full. Blank: already over. A full or closed window, or the one under way, can still be
            booked.
          </p>
        </>
      )}
      <label className="f bk-jump">
        Jump to a day
        <input
          className="field"
          type="date"
          min={today}
          max={last}
          value={chosen.date}
          onChange={(e) => {
            const v = e.target.value;
            if (!v) return;
            if (weekPageOf(today, v) === null)
              return setJumpError(`Pick a day from today to ${dayLabel(last, today)}.`);
            setJumpError(null);
            setFocus(null);
            onJump(v);
          }}
        />
      </label>
      {jumpError && <p className="muted small">{jumpError}</p>}
    </div>
  );
}
